import { useState } from 'react';
import { Menu, Trash2, Clock, Users } from 'lucide-react';
import { cn, getDefensiveSignal } from '../lib/utils';
import { PITCH_COUNT_RULESETS } from '../lib/pitch-count-rules';

export const FieldNode = ({ pos, player, className, onClick, isSelected, isDropTarget, pitchBadge }: { pos: string, player: any, className: string, onClick: () => void, isSelected?: boolean, isDropTarget?: boolean, pitchBadge?: string }) => (
  <div 
    onClick={onClick}
    className={cn(
      "absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center cursor-pointer transition-all",
      className
    )}
  >
    <div className={cn(
      "w-10 h-10 rounded-full flex items-center justify-center text-xs font-black border-2 shadow-xl transition-all",
      isSelected ? "bg-emerald-500 border-emerald-400 text-ink scale-110 shadow-[0_0_20px_rgba(16,185,129,0.4)] z-10" :
      isDropTarget ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-500 border-dashed animate-pulse" :
      player ? "bg-surface border-track text-ink hover:border-emerald-500/50" : 
      "bg-raised/80 border-hairline text-ink-2 hover:border-ink-mute border-dashed"
    )}>
      {player ? (
        <span className="text-sm font-black">#{player.number || '??'}</span>
      ) : (
        <span className="text-[10px] opacity-40">{pos}</span>
      )}
    </div>
    {player && (
      <div className={cn(
        "mt-1 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-tight flex items-center space-x-1 shadow-lg transition-all whitespace-nowrap",
        isSelected ? "bg-emerald-500 text-ink scale-105" : "bg-surface text-ink-2 border border-hairline"
      )}>
        <span className={cn("font-bold", isSelected ? "text-ink/60" : "text-emerald-500")}>{pos}</span>
        <span className="w-px h-2 bg-track/50" />
        <span className={cn("truncate max-w-[65px] text-ink")}>
          {(() => {
            const nameParts = (player.name || '').trim().split(/\s+/).filter(Boolean);
            if (nameParts.length > 1) {
              return `${nameParts[0]} ${nameParts[nameParts.length - 1][0]}.`;
            }
            return nameParts[0] || 'Unknown';
          })()}
        </span>
        {pitchBadge && (
          <>
            <span className="w-px h-2 bg-track/50" />
            <span className={cn("font-black", isSelected ? "text-ink/80" : "text-gold-ink")}>{pitchBadge}</span>
          </>
        )}
      </div>
    )}
  </div>
);

type ActiveTab = 'defense' | 'pitch_tracker' | 'lineup';

interface DiamondViewProps {
  team: any;
  currentPitcher: any;
  currentPitchCount: number;
  getPlayerAt: (pos: string) => any;
  selectedPlayerId: string | null;
  handlePositionClick: (pos: string) => void;
  handleBenchClick: () => void;
  handlePlayerClick: (id: string) => void;
  lateArrivals?: string[];
  benchPlayers: any[];
  inningLabel: string;
  gameInfo: { opponent: string; isHome: boolean; date: string; isTournament: boolean };
  onManageRoster?: () => void;
  onPauseExit: () => void;
  onEndGame: () => void;
  onDiscardGame: () => void;
  activeTab: ActiveTab;
  onTabChange: (tab: ActiveTab) => void;
  rulesetOverride?: string;
  effectiveCustomRules?: any;
  isHeadCoach?: boolean;
}

export const DiamondView = ({
  team,
  currentPitcher,
  currentPitchCount,
  getPlayerAt,
  selectedPlayerId,
  handlePositionClick,
  handleBenchClick,
  handlePlayerClick,
  lateArrivals = [],
  benchPlayers,
  inningLabel,
  gameInfo,
  onManageRoster,
  onPauseExit,
  onEndGame,
  onDiscardGame,
  activeTab,
  onTabChange,
  rulesetOverride,
  effectiveCustomRules,
  isHeadCoach = true,
}: DiamondViewProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Reference daily-pitch ceiling for the live count color cue. Honors the
  // active ruleset so a tournament's custom dailyMax (e.g. 10) colors the count
  // correctly instead of always scaling against the season default / 85.
  //   custom ruleset → effectiveCustomRules.dailyMax
  //   league ruleset → that ruleset's age-group dailyMax
  //   fallback       → team season maxPitchesPerDay, then 85
  const pitchRefMax = (() => {
    const rs = rulesetOverride ? PITCH_COUNT_RULESETS[rulesetOverride] : null;
    if (rs?.id === 'custom' && effectiveCustomRules?.dailyMax) return effectiveCustomRules.dailyMax;
    const ageGroup = team?.settings?.pitchCountAgeGroup || team?.ageGroup || '12U';
    const ar = rs?.ageGroups?.[ageGroup] || rs?.ageGroups?.['12U'];
    if (ar?.dailyMax) return ar.dailyMax;
    return team?.settings?.maxPitchesPerDay || 85;
  })();

  return (
    <div className="bg-surface rounded-3xl p-3 border border-hairline">
      {/* Top bar — menu left, pill tabs center, inning right */}
      <div className="flex items-center justify-between px-1 pb-2 gap-2">
        <div className="relative shrink-0">
          <button
            onClick={() => setIsMenuOpen(v => !v)}
            className="p-1.5 bg-hairline rounded-lg"
            aria-label="Game menu"
          >
            <Menu className="w-4 h-4 text-ink-2" />
          </button>

          {isMenuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
              <div className="absolute top-full left-0 mt-2 z-50 w-52 bg-surface border border-track rounded-2xl shadow-2xl overflow-hidden">
                {gameInfo.isTournament && (
                  <div className="px-4 pt-3 pb-1">
                    <span className="text-[8px] px-1.5 py-0.5 bg-amber-500 text-ink font-black uppercase rounded">
                      Tournament
                    </span>
                  </div>
                )}
                <div className="px-4 pt-2 pb-3 border-b border-hairline">
                  <p className="text-sm font-black italic uppercase leading-tight">
                    {gameInfo.isHome ? 'Home vs ' : 'Away @ '}{gameInfo.opponent}
                  </p>
                </div>
                <div className="p-2 space-y-1">
                  {isHeadCoach && onManageRoster && (
                    <button
                      onClick={() => { setIsMenuOpen(false); onManageRoster(); }}
                      className="w-full text-left px-3 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest text-ink-2 hover:bg-hairline hover:text-ink transition-colors flex items-center space-x-2"
                    >
                      <Users className="w-3.5 h-3.5 shrink-0" />
                      <span>Add / Remove Player</span>
                    </button>
                  )}
                  <button
                    onClick={() => { setIsMenuOpen(false); onPauseExit(); }}
                    className="w-full text-left px-3 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest text-ink-2 hover:bg-hairline hover:text-ink transition-colors"
                  >
                    Pause &amp; Exit
                  </button>
                  {isHeadCoach && (
                    <button
                      onClick={() => { setIsMenuOpen(false); onDiscardGame(); }}
                      className="w-full text-left px-3 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest text-gold-ink hover:bg-amber-500/10 hover:text-amber-300 transition-colors flex items-center space-x-2"
                    >
                      <Trash2 className="w-3.5 h-3.5 shrink-0" />
                      <span>Discard Game</span>
                    </button>
                  )}
                  {isHeadCoach && (
                    <>
                      <div className="border-t border-hairline my-1" />
                      <button
                        onClick={() => { setIsMenuOpen(false); onEndGame(); }}
                        className="w-full text-left px-3 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest bg-red-500/10 text-danger hover:bg-red-500/20 hover:text-red-300 transition-colors"
                      >
                        <div>End Game</div>
                        <div className="text-[8px] font-bold text-ink-2 normal-case tracking-normal mt-0.5">Finalizes score &amp; stats</div>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Pill tabs */}
        <div className="flex flex-1 justify-center">
          <div className="flex bg-hairline rounded-full p-0.5 gap-0">
            {(['defense', 'pitch_tracker', 'lineup'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => onTabChange(tab)}
                className={cn(
                  "px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest transition-colors whitespace-nowrap",
                  activeTab === tab ? "bg-ink text-surface" : "text-ink-2 hover:text-ink"
                )}
              >
                {tab === 'defense' ? 'Defense' : tab === 'pitch_tracker' ? 'Pitches' : 'Lineup'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-1.5 shrink-0">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-[10px] font-black uppercase tracking-widest">{inningLabel}</span>
        </div>
      </div>
      {activeTab === 'defense' && <div className="relative w-full aspect-square max-w-lg diamond-fit mx-auto mt-0 mb-2">
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
          {/* Outfield Grass */}
          <path d="M 50 95 L 5 50 C 20 10, 80 10, 95 50 Z" className="fill-emerald-900/20 stroke-emerald-500/30" strokeWidth="0.5" />
          {/* Infield Dirt */}
          <polygon points="50,85 25,60 50,35 75,60" className="fill-amber-900/20 stroke-amber-700/30" strokeWidth="0.5" />
          {/* Infield Grass */}
          <polygon points="50,80 30,60 50,40 70,60" className="fill-emerald-900/20" />
          {/* Bases */}
          <polygon points="50,87 48,85 50,83 52,85" fill="white" opacity="0.8" />
          <polygon points="77,60 75,58 73,60 75,62" fill="white" opacity="0.8" />
          <polygon points="50,33 48,35 50,37 52,35" fill="white" opacity="0.8" />
          <polygon points="23,60 25,58 27,60 25,62" fill="white" opacity="0.8" />
          {/* Pitcher's Mound */}
          <circle cx="50" cy="60" r="4" className="fill-amber-900/40" />
          <rect x="48" y="59.5" width="4" height="1" fill="white" opacity="0.8" />
          {/* Home Plate */}
          <polygon points="49,85 51,85 51,86 50,87 49,86" fill="white" opacity="0.8" />
        </svg>

        {/* Positions */}
        {(!team.settings?.noCatcher) && <FieldNode pos="C" player={getPlayerAt('C')} isSelected={selectedPlayerId === getPlayerAt('C')?.id} isDropTarget={!!selectedPlayerId && selectedPlayerId !== getPlayerAt('C')?.id} onClick={() => handlePositionClick('C')} className="top-[92%] left-[50%]" />}
        
        {(!team.settings?.noPitcher) && (
          <div className="absolute top-[60%] left-[50%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center pointer-events-none">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-black border border-gold/50 bg-gold-soft text-ink shadow-xl transition-all">
              {getPlayerAt('P') ? (
                <span className="text-sm font-black">#{getPlayerAt('P').number || '??'}</span>
              ) : (
                <span className="text-[10px] opacity-40">P</span>
              )}
            </div>
            {getPlayerAt('P') && (
              <div className="mt-1 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-tight flex items-center space-x-1 bg-surface text-ink-2 border border-hairline shadow-lg">
                <span>P</span>
                <span className="w-px h-2 bg-track/50" />
                <span className="truncate max-w-[65px]">{getPlayerAt('P').name}</span>
                {currentPitchCount > 0 && (
                  <>
                    <span className="w-px h-2 bg-track/50" />
                    <span className={cn(
                      "font-black",
                      (currentPitchCount / pitchRefMax) > 0.8
                        ? "text-danger"
                        : (currentPitchCount / pitchRefMax) > 0.5
                        ? "text-gold-ink"
                        : "text-accent-ink"
                    )}>{currentPitchCount}p</span>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <FieldNode pos="1B" player={getPlayerAt('1B')} isSelected={selectedPlayerId === getPlayerAt('1B')?.id} isDropTarget={!!selectedPlayerId && selectedPlayerId !== getPlayerAt('1B')?.id} onClick={() => handlePositionClick('1B')} className="top-[55%] left-[75%]" />
        <FieldNode pos="2B" player={getPlayerAt('2B')} isSelected={selectedPlayerId === getPlayerAt('2B')?.id} isDropTarget={!!selectedPlayerId && selectedPlayerId !== getPlayerAt('2B')?.id} onClick={() => handlePositionClick('2B')} className="top-[40%] left-[65%]" />
        <FieldNode pos="SS" player={getPlayerAt('SS')} isSelected={selectedPlayerId === getPlayerAt('SS')?.id} isDropTarget={!!selectedPlayerId && selectedPlayerId !== getPlayerAt('SS')?.id} onClick={() => handlePositionClick('SS')} className="top-[40%] left-[35%]" />
        <FieldNode pos="3B" player={getPlayerAt('3B')} isSelected={selectedPlayerId === getPlayerAt('3B')?.id} isDropTarget={!!selectedPlayerId && selectedPlayerId !== getPlayerAt('3B')?.id} onClick={() => handlePositionClick('3B')} className="top-[55%] left-[25%]" />
        <FieldNode pos="LF" player={getPlayerAt('LF')} isSelected={selectedPlayerId === getPlayerAt('LF')?.id} isDropTarget={!!selectedPlayerId && selectedPlayerId !== getPlayerAt('LF')?.id} onClick={() => handlePositionClick('LF')} className="top-[25%] left-[20%]" />
        {team.settings?.fourOutfielders ? (
          <>
            <FieldNode pos="LCF" player={getPlayerAt('LCF')} isSelected={selectedPlayerId === getPlayerAt('LCF')?.id} isDropTarget={!!selectedPlayerId && selectedPlayerId !== getPlayerAt('LCF')?.id} onClick={() => handlePositionClick('LCF')} className="top-[15%] left-[35%]" />
            <FieldNode pos="RCF" player={getPlayerAt('RCF')} isSelected={selectedPlayerId === getPlayerAt('RCF')?.id} isDropTarget={!!selectedPlayerId && selectedPlayerId !== getPlayerAt('RCF')?.id} onClick={() => handlePositionClick('RCF')} className="top-[15%] left-[65%]" />
          </>
        ) : (
          <FieldNode pos="CF" player={getPlayerAt('CF')} isSelected={selectedPlayerId === getPlayerAt('CF')?.id} isDropTarget={!!selectedPlayerId && selectedPlayerId !== getPlayerAt('CF')?.id} onClick={() => handlePositionClick('CF')} className="top-[15%] left-[50%]" />
        )}
        <FieldNode pos="RF" player={getPlayerAt('RF')} isSelected={selectedPlayerId === getPlayerAt('RF')?.id} isDropTarget={!!selectedPlayerId && selectedPlayerId !== getPlayerAt('RF')?.id} onClick={() => handlePositionClick('RF')} className="top-[25%] left-[80%]" />

        {/* Pitcher name + pitch count — combined bottom-left card */}
        {currentPitcher && (
          <div className="absolute bottom-2 left-2 bg-raised/90 border border-track/60 rounded-xl px-3.5 py-2 pointer-events-none">
            <div className="text-[9px] font-bold text-ink-2 uppercase tracking-widest">pitcher</div>
            <div className="flex items-baseline gap-2">
              <div className="text-base font-black italic uppercase leading-none text-ink">
                {(() => {
                  const parts = (currentPitcher.name || '').trim().split(/\s+/).filter(Boolean);
                  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0] || '—';
                })()}
              </div>
              {currentPitchCount > 0 && (() => {
                const pct = currentPitchCount / pitchRefMax;
                const countColor = pct > 0.8 ? 'text-danger' : pct > 0.5 ? 'text-gold-ink' : 'text-accent-ink';
                return <span className={cn('text-base font-black italic leading-none', countColor)}>{currentPitchCount}p</span>;
              })()}
            </div>
          </div>
        )}
      </div>}

      {/* Bench — only on defense tab */}
      {activeTab === 'defense' && benchPlayers.length > 0 && (
        <div
          className={cn("pt-2 border-t border-hairline/60", selectedPlayerId ? "cursor-pointer" : "")}
          onClick={selectedPlayerId ? handleBenchClick : undefined}
        >
          <div className="text-[9px] font-black text-ink-2 uppercase tracking-widest mb-2 px-1">Bench</div>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {benchPlayers.map((player: any) => (
              <div
                key={player.id}
                className={cn(
                  "flex-shrink-0 flex items-center rounded-xl border transition-colors overflow-hidden",
                  selectedPlayerId === player.id
                    ? "border-emerald-500 bg-emerald-500/10"
                    : "border-hairline bg-raised"
                )}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); handlePlayerClick(player.id); }}
                  className="flex items-center gap-1.5 px-3 py-2 text-left hover:bg-hairline/50 transition-colors"
                >
                  {lateArrivals.includes(player.id)
                    ? <Clock className="w-2.5 h-2.5 flex-shrink-0 text-amber-400" />
                    : <div className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", getDefensiveSignal(player, 'BENCH'))} />
                  }
                  <span className={cn("text-[10px] font-black uppercase", lateArrivals.includes(player.id) && "text-amber-300")}>
                    {player.name.split(' ')[0]}
                  </span>
                  <span className="text-[9px] font-bold text-ink-2">#{player.number}</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};